import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import {
  ArrowUpRight,
  Bug,
  ChevronDown,
  Clock3,
  FileDiff,
  GitBranch,
  Package2,
  RefreshCw,
  UserRound,
} from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import FileChangeDetailCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeDetailCard';
import FileChangeFeedSkeleton from '@/Modules/Core/Components/FileChangeFeed/FileChangeFeedSkeleton';
import FileChangeGroupCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeGroupCard';
import FileChangeItemCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeItemCard';
import FileChangeStatCard from '@/Modules/Core/Components/FileChangeFeed/FileChangeStatCard';
import FileChangeToolbar from '@/Modules/Core/Components/FileChangeFeed/FileChangeToolbar';
import {
  getActionMeta,
  summarizeItems,
  toTimestamp,
} from '@/Modules/Core/Components/FileChangeFeed/fileChangeFeedUtils';
import useAffectedFileDetailCatalog from '../hooks/useAffectedFileDetailCatalog';

const INITIAL_VISIBLE_ITEMS = 20;
const SEARCH_DEBOUNCE_DELAY = 700;
const VISIBLE_ITEMS_STEP = 20;

const GROUP_BY_OPTIONS = [
  { id: 'rollout', label: 'Group by Rollout' },
  { id: 'bug', label: 'Group by Bug' },
];

const getGroupingButtonClassName = (activeGroupBy, optionId) => {
  const isActive = activeGroupBy === optionId;

  if (!isActive) {
    return 'border-border/40 bg-transparent text-muted-foreground hover:border-border/60 hover:text-foreground';
  }

  return 'border-primary/35 bg-primary/12 text-primary shadow-sm dark:text-primary-foreground';
};

const groupAffectedFileDetails = (items, groupBy) => {
  const grouped = new Map();

  items.forEach((item) => {
    const groupValue =
      groupBy === 'rollout'
        ? item.rollout || 'Unknown rollout'
        : item.bugId || 'Unassigned bug';
    const groupId = `${groupBy}:${groupValue}`;

    if (!grouped.has(groupId)) {
      grouped.set(groupId, {
        id: groupId,
        title: groupValue,
        bugUrl: groupBy === 'bug' ? item.bugUrl : '',
        items: [],
        rollouts: new Set(),
        bugs: new Set(),
        authors: new Set(),
        latestCommitDate: '',
        deletionCount: 0,
      });
    }

    const group = grouped.get(groupId);
    group.items.push(item);

    if (item.rollout) {
      group.rollouts.add(item.rollout);
    }

    if (item.bugId) {
      group.bugs.add(item.bugId);
    }

    if (item.author) {
      group.authors.add(item.author);
    }

    if (getActionMeta(item.action).tone === 'delete') {
      group.deletionCount += 1;
    }

    if (toTimestamp(item.commitDate) > toTimestamp(group.latestCommitDate)) {
      group.latestCommitDate = item.commitDate;
    }
  });

  return Array.from(grouped.values())
    .map((group) => {
      const sortedItems = [...group.items].sort((first, second) => {
        const dateDifference = toTimestamp(second.commitDate) - toTimestamp(first.commitDate);

        if (dateDifference !== 0) {
          return dateDifference;
        }

        return String(first.filePath || '').localeCompare(String(second.filePath || ''));
      });

      return {
        ...group,
        items: sortedItems,
        itemCount: sortedItems.length,
        latestCommitDateLabel: sortedItems[0]?.commitDateLabel || 'Unknown date',
        bugSummary: summarizeItems(group.bugs),
        rolloutSummary: summarizeItems(group.rollouts),
        authorSummary: summarizeItems(group.authors),
      };
    })
    .filter(Boolean)
    .sort((first, second) => {
      const dateDifference =
        toTimestamp(second.latestCommitDate) - toTimestamp(first.latestCommitDate);

      if (dateDifference !== 0) {
        return dateDifference;
      }

      return String(first.title || '').localeCompare(String(second.title || ''));
    });
};

const buildStats = (items) => ({
  totalChanges: items.length,
  uniqueRollouts: new Set(items.map((item) => item.rollout).filter(Boolean)).size,
  uniqueBugs: new Set(items.map((item) => item.bugId).filter(Boolean)).size,
  authors: new Set(items.map((item) => item.author).filter(Boolean)).size,
});

const ObjectChangelog = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [groupBy, setGroupBy] = React.useState('rollout');
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const [expandedItems, setExpandedItems] = React.useState({});
  const [visibleItemsByGroup, setVisibleItemsByGroup] = React.useState({});
  const [loadingMoreItemsByGroup, setLoadingMoreItemsByGroup] = React.useState({});
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState({
    type: 'error',
    message: '',
  });

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm((current) => (
        current === searchTerm ? current : searchTerm
      ));
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const {
    affectedFileDetails,
    errorMessage,
    hasMore,
    loading,
    loadingMore,
    refreshing,
    loadMoreAffectedFileDetails,
    refreshAffectedFileDetails,
    reportedTotal,
  } = useAffectedFileDetailCatalog({
    searchTerm: debouncedSearchTerm,
  });

  const clearAlert = React.useCallback(() => {
    setAlertMessage({
      type: 'error',
      message: '',
    });
  }, []);

  const showAlert = React.useCallback((message, type = 'error') => {
    setAlertMessage({
      type,
      message,
    });
  }, []);

  React.useEffect(() => {
    if (errorMessage) {
      showAlert(errorMessage);
    } else {
      clearAlert();
    }
  }, [clearAlert, errorMessage, showAlert]);

  const stats = React.useMemo(
    () => buildStats(affectedFileDetails),
    [affectedFileDetails]
  );

  const groups = React.useMemo(
    () => groupAffectedFileDetails(affectedFileDetails, groupBy),
    [affectedFileDetails, groupBy]
  );

  React.useEffect(() => {
    const visibleGroupIds = new Set(groups.map((group) => group.id));

    setExpandedGroups((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([groupId, isOpen]) => visibleGroupIds.has(groupId) && isOpen)
      )
    );

    setExpandedItems((current) => {
      const visibleItemIds = new Set(
        groups.flatMap((group) => group.items.map((item) => String(item.key)))
      );

      return Object.fromEntries(
        Object.entries(current).filter(([itemId, isOpen]) => visibleItemIds.has(itemId) && isOpen)
      );
    });

    setVisibleItemsByGroup((current) => {
      const nextState = {};

      groups.forEach((group) => {
        nextState[group.id] = Math.min(
          current[group.id] ?? INITIAL_VISIBLE_ITEMS,
          group.itemCount
        );
      });

      return nextState;
    });

    setLoadingMoreItemsByGroup((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([groupId, isLoading]) => visibleGroupIds.has(groupId) && isLoading
        )
      )
    );
  }, [groups]);

  const visibleGroups = groups;

  const filteredItemCount = React.useMemo(
    () => groups.reduce((total, group) => total + group.itemCount, 0),
    [groups]
  );

  const handleRefresh = React.useCallback(async () => {
    clearAlert();
    await refreshAffectedFileDetails();
  }, [clearAlert, refreshAffectedFileDetails]);

  const handleLoadMoreChanges = React.useCallback(async () => {
    clearAlert();
    await loadMoreAffectedFileDetails();
  }, [clearAlert, loadMoreAffectedFileDetails]);

  const toggleGroup = React.useCallback((groupId) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));

    setVisibleItemsByGroup((current) => ({
      ...current,
      [groupId]: current[groupId] ?? INITIAL_VISIBLE_ITEMS,
    }));
  }, []);

  const toggleItem = React.useCallback((itemId) => {
    setExpandedItems((current) => ({
      ...current,
      [itemId]: !current[itemId],
    }));
  }, []);

  const handleLoadMoreItems = React.useCallback((groupId, itemCount) => {
    setLoadingMoreItemsByGroup((current) => ({
      ...current,
      [groupId]: true,
    }));

    window.setTimeout(() => {
      setVisibleItemsByGroup((current) => ({
        ...current,
        [groupId]: Math.min(
          (current[groupId] ?? INITIAL_VISIBLE_ITEMS) + VISIBLE_ITEMS_STEP,
          itemCount
        ),
      }));

      setLoadingMoreItemsByGroup((current) => ({
        ...current,
        [groupId]: false,
      }));
    }, 120);
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        clearAlert();
      }}
    >
      <div className="tw rollout-list-page">
        <AppPageSection>
          {alertMessage.message ? (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
              {alertMessage.message}
            </div>
          ) : null}

          {loading && affectedFileDetails.length === 0 ? (
            <FileChangeFeedSkeleton filterChipCount={2} />
          ) : (
            <div className="space-y-6">
              <FileChangeToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search... Examples: bug:13887 file:debug.js, path:customer/db, rollout:July-2026"
                onRefresh={handleRefresh}
                refreshing={loading || loadingMore || refreshing}
                refreshLabel="Refresh object change logs"
                options={GROUP_BY_OPTIONS}
                activeOption={groupBy}
                onOptionChange={setGroupBy}
                getOptionClassName={getGroupingButtonClassName}
              />

              <div className="flex flex-col gap-2 text-[12px] text-muted-foreground md:flex-row md:items-center md:justify-between">
                <span>
                  Showing {filteredItemCount} loaded changes across {groups.length} {groupBy}s
                </span>
                <span>
                  Loaded {affectedFileDetails.length} of {reportedTotal || affectedFileDetails.length} changes
                </span>
              </div>

              {groups.length === 0 ? (
                <EmptyState
                  icon={FileDiff}
                  title={
                    affectedFileDetails.length === 0
                      ? 'No object change logs available'
                      : 'No object change logs matched your filters'
                  }
                  description={
                    affectedFileDetails.length === 0
                      ? 'Object-level change history will appear here once data is available.'
                      : 'Try a different search expression such as bug:13887, file:debug.js, or rollout:July-2026.'
                  }
                />
              ) : (
                <>
                  <div className="space-y-5">
                    {visibleGroups.map((group) => {
                      const isGroupExpanded = Boolean(expandedGroups[group.id]);
                      const visibleItemLimit =
                        visibleItemsByGroup[group.id] ?? INITIAL_VISIBLE_ITEMS;
                      const visibleItems = group.items.slice(0, visibleItemLimit);
                      const hasMoreItems = group.itemCount > visibleItems.length;
                      const isLoadingMoreItems = Boolean(loadingMoreItemsByGroup[group.id]);
                      const groupMetaLabel =
                        groupBy === 'rollout' ? group.bugSummary : group.rolloutSummary;

                      return (
                        <FileChangeGroupCard
                          key={group.id}
                          icon={groupBy === 'rollout' ? Package2 : Bug}
                          title={group.title}
                          linkHref={groupBy === 'bug' ? group.bugUrl : ''}
                          linkLabel="Open bug"
                          deletionCount={group.deletionCount}
                          metaItems={[
                            {
                              icon: groupBy === 'rollout' ? Bug : Package2,
                              label: groupMetaLabel,
                            },
                            {
                              icon: UserRound,
                              label: group.authorSummary,
                            },
                            {
                              icon: Clock3,
                              label: group.latestCommitDateLabel,
                            },
                          ]}
                          countValue={group.itemCount}
                          countLabel="changes"
                          isExpanded={isGroupExpanded}
                          onToggle={() => toggleGroup(group.id)}
                        >
                          <div className="border-t border-border/35 px-4 py-4">
                            <div className="flex items-center justify-between gap-3 px-2 pb-3 text-[12px] text-muted-foreground">
                              <span>Showing {visibleItems.length} changes in this group</span>
                              {group.itemCount > visibleItems.length ? (
                                <span>{group.itemCount - visibleItems.length} more hidden</span>
                              ) : null}
                            </div>

                            {visibleItems.map((item, index) => {
                              const actionMeta = getActionMeta(item.action);
                              const isItemExpanded = Boolean(expandedItems[String(item.key)]);
                              const isLastVisibleRow =
                                index === visibleItems.length - 1 && !hasMoreItems;

                              return (
                                <FileChangeItemCard
                                  key={item.key}
                                  filePath={item.filePath}
                                  summaryMetaItems={[
                                    {
                                      icon: groupBy === 'rollout' ? Bug : Package2,
                                      label:
                                        groupBy === 'rollout'
                                          ? item.bugId || '--'
                                          : item.rollout,
                                    },
                                    {
                                      icon: Clock3,
                                      label: item.commitDateLabel,
                                    },
                                    {
                                      icon: UserRound,
                                      label: item.author,
                                    },
                                  ]}
                                  actionMeta={actionMeta}
                                  isExpanded={isItemExpanded}
                                  isLastVisibleRow={isLastVisibleRow}
                                  onToggle={() => toggleItem(String(item.key))}
                                  details={
                                    <>
                                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-4">
                                        <FileChangeDetailCard
                                          label="Full Path"
                                          value={item.filePath || '--'}
                                          valueClassName="break-all"
                                        />
                                        <FileChangeDetailCard
                                          label="Bug"
                                          value={item.bugId || '--'}
                                          valueClassName="break-all"
                                        />
                                        <FileChangeDetailCard
                                          label="Rollout"
                                          value={item.rollout || '--'}
                                          valueClassName="break-all"
                                        />
                                        <FileChangeDetailCard
                                          label="Created Date"
                                          value={item.commitDateLabel}
                                        />
                                      </div>

                                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                        <FileChangeDetailCard
                                          label="Author"
                                          value={item.author}
                                          valueClassName="break-all"
                                        />
                                        <FileChangeDetailCard
                                          label="Action"
                                          value={(
                                            <span
                                              className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${actionMeta.className}`}
                                            >
                                              {actionMeta.label}
                                            </span>
                                          )}
                                        />
                                      </div>
                                    </>
                                  }
                                  actions={
                                    <>
                                      {item.fileUrl ? (
                                        <a
                                          href={item.fileUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex h-10 items-center gap-2 rounded-xl border border-border/30 bg-transparent px-4 text-sm font-medium text-foreground transition-colors hover:border-border/60 hover:bg-primary/3"
                                        >
                                          <ArrowUpRight className="h-4 w-4" />
                                          Open file
                                        </a>
                                      ) : null}

                                      {item.bugUrl ? (
                                        <a
                                          href={item.bugUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex h-10 items-center gap-2 rounded-xl border border-border/30 bg-transparent px-4 text-sm font-medium text-foreground transition-colors hover:border-border/60 hover:bg-primary/3"
                                        >
                                          <GitBranch className="h-4 w-4" />
                                          Open bug
                                        </a>
                                      ) : null}
                                    </>
                                  }
                                />
                              );
                            })}

                            {hasMoreItems ? (
                              <div className="flex flex-col items-center gap-2 border-t border-border/20 px-4 py-4">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-10 rounded-xl border-border/35 bg-transparent px-4 hover:bg-accent/20"
                                  onClick={() => handleLoadMoreItems(group.id, group.itemCount)}
                                  disabled={isLoadingMoreItems}
                                >
                                  {isLoadingMoreItems ? (
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                  ) : (
                                    <ChevronDown className="mr-2 h-4 w-4" />
                                  )}
                                  Show more loaded changes
                                </Button>
                                <div className="text-[12px] text-muted-foreground">
                                  Showing {visibleItems.length} of {group.itemCount} loaded changes
                                </div>
                              </div>
                            ) : group.itemCount > INITIAL_VISIBLE_ITEMS ? (
                              <div className="border-t border-border/20 px-4 py-3 text-center text-[12px] text-muted-foreground">
                                Showing all {group.itemCount} changes in this group
                              </div>
                            ) : null}
                          </div>
                        </FileChangeGroupCard>
                      );
                    })}
                  </div>

                </>
              )}

              {hasMore ? (
                <div className="flex flex-col items-center gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 rounded-xl border-border/40 bg-transparent px-5 hover:bg-accent/20"
                    onClick={handleLoadMoreChanges}
                    disabled={loading || loadingMore || refreshing}
                  >
                    {loadingMore ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="mr-2 h-4 w-4" />
                    )}
                    Load more changes
                  </Button>
                  <div className="text-[12px] text-muted-foreground">
                    Loaded {affectedFileDetails.length} of {reportedTotal || affectedFileDetails.length} changes
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </AppPageSection>
      </div>
    </ErrorBoundary>
  );
};

export default ObjectChangelog;
